import type { PresignResponse, QuotaCheckResponse } from "@poseref/shared";
import { apiFetch } from "./client.ts";

/**
 * Get a presigned URL for uploading a file to R2.
 * The key should include a path prefix, e.g. "scenes/abc123.pr"
 */
export async function getPresignedUploadUrl(
  key: string,
  contentType: string,
): Promise<PresignResponse> {
  return apiFetch<PresignResponse>("/api/upload/presign", {
    method: "POST",
    body: { key, contentType },
  });
}

/**
 * Check whether a file with the given SHA-256 hash already exists in R2.
 * Used for deduplication of .blend source file uploads.
 */
export async function checkFileHash(
  hash: string,
): Promise<{ exists: boolean; key: string | null }> {
  return apiFetch<{ exists: boolean; key: string | null }>(
    `/api/upload/check-hash?hash=${encodeURIComponent(hash)}`,
  );
}

/**
 * Get a presigned inline download URL for displaying an image in an <img> tag.
 * Uses 1-hour expiry with no Content-Disposition attachment header.
 */
export async function getInlineDownloadUrl(key: string): Promise<string> {
  const res = await apiFetch<{ url: string }>(
    "/api/upload/inline-download-url",
    {
      method: "POST",
      body: { key },
    },
  );
  return res.url;
}

/**
 * Batch-resolve multiple R2 keys to inline download URLs.
 */
export async function getInlineDownloadUrls(
  keys: string[],
): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  await Promise.all(
    keys.map(async (key) => {
      const url = await getInlineDownloadUrl(key);
      results.set(key, url);
    }),
  );
  return results;
}

/**
 * Check if a file of the given size would exceed the user's storage quota.
 *
 * When the quota is exceeded, a modal dialog is shown automatically and an
 * error is thrown so callers can rely on a simple try/catch without needing
 * per-site UI logic.
 */
export async function checkQuota(size: number): Promise<QuotaCheckResponse> {
  const res = await apiFetch<QuotaCheckResponse>("/api/upload/check-quota", {
    method: "POST",
    body: { size },
  });

  if (!res.allowed) {
    const { showStorageQuotaDialog } = await import(
      "../ui/storageQuotaDialogBus.ts"
    );
    showStorageQuotaDialog(res.usedBytes, res.limitBytes, res.tier, size);
    throw new Error("Storage quota exceeded");
  }

  return res;
}

/**
 * Find an asset (object/collection/blend) by its content hash.
 * Returns the item ID and a presigned download URL for the asset's file.
 */
export async function findItemByHash(
  hash: string,
): Promise<{ exists: boolean; itemId: string | null; fileUrl: string | null }> {
  return apiFetch<{
    exists: boolean;
    itemId: string | null;
    fileUrl: string | null;
  }>(`/api/upload/find-by-hash?hash=${encodeURIComponent(hash)}`);
}
